import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Grid, Typography, Button, useTheme, Box } from '@mui/material';
import { GlobalStyles } from '@mui/system';
import {
  DataGrid,
  GridColDef,
  GridRowId,
  DataGridProps,
} from '@mui/x-data-grid';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  loggedInUserState,
  snackbarOpenState,
  snackbarTextState,
} from '../../domain/app';
import { User } from '../../types/user';
import { FollowButton } from '../Shared/FollowButton';

import { SocialMediaIcons } from './SocialMediaIcons';

import {
  createNewTopic,
  getTopicSubscriptions,
  getUserChatTopicListWithFilter,
} from '@app/adapter/chat-service';
import { getUserFollowList } from '@app/adapter/user-service';
import { organization } from '@app/domain/organization';
import { useProductsAdd } from '@app/hooks/catalog';
import { Product } from '@app/types/catalog';
import { OrderLocalized } from '@app/types/order';
import { getAge } from '@app/utils';
import { getProductDayWeekTitle } from '@app/utils/catalog';
import { formatDate, formatTime } from '@app/utils/format';

export type OrderItem = Omit<
  Partial<OrderLocalized>,
  'customer' | 'lineItem'
> & {
  customer?: Partial<OrderLocalized['customer']>;
  lineItem?: Partial<OrderLocalized['lineItem']>;
};

type OrderTableComponentProps = {
  loading?: boolean;
  mode?: 'order' | 'user' | 'admin';
  onPaginationModelChange?: DataGridProps['onPaginationModelChange'];
  orders: OrderItem[];
  paginationModel?: DataGridProps['paginationModel'];
  rowCount?: number;
  selectionIDs?: string[];
  setSelectedIDs?: React.Dispatch<React.SetStateAction<string[]>>;
};

export const OrderTableComponent: React.FC<OrderTableComponentProps> = ({
  setSelectedIDs,
  selectionIDs,
  paginationModel,
  rowCount,
  loading: loadingProp,
  onPaginationModelChange,
  orders,
  mode,
}) => {
  const theme = useTheme();
  const navigateTo = useNavigate();
  const setSnackbarOpen = useSetRecoilState(snackbarOpenState);
  const setSnackbarText = useSetRecoilState(snackbarTextState);
  const [followList, setFollowList] = useState([] as string[]);
  const loggedInUser: User | null = useRecoilValue(loggedInUserState);
  const organizationState = useRecoilValue(organization);
  const [isLoading, setIsLoading] = useState(false);
  const productIds = useMemo(
    () => orders.map((o) => o.lineItem?.product as string).filter((p) => p),
    [orders]
  );
  const products = useProductsAdd(productIds, mode === 'admin');

  const loadFollowing = useCallback(async () => {
    if (!loggedInUser?.id || !orders.length) {
      return;
    }
    try {
      const userIds = orders
        .map((o) => o.customer?.user?.id ?? '')
        .filter((id) => id);
      if (!userIds.length) {
        return;
      }
      const result = await getUserFollowList(loggedInUser.id, 'user', {
        followIds: userIds,
      });
      setFollowList(result.value.map((f) => f.followId));
    } catch (error) {
      setSnackbarText(
        `フォローの取得に失敗しました, ${
          error instanceof Error ? error.message : error
        }`
      );
      setSnackbarOpen(true);
    }
  }, [loggedInUser, orders, setSnackbarOpen, setSnackbarText]);

  useEffect(() => {
    void loadFollowing();
  }, [loadFollowing]);

  // メッセージボタン押下時にTopicがあればチャット画面へ、なければTopicとサブスク作成した上でチャット画面へ
  const handleChatButtonClick = async (userId: string) => {
    setIsLoading(true);

    if (!loggedInUser || !loggedInUser.id) {
      console.error('ログインユーザーのIDが存在しません。');
      setIsLoading(false);
      return;
    }

    try {
      // サブスクしている全Topic取得
      const response = await getUserChatTopicListWithFilter(loggedInUser.id, {
        expandUser: true,
      });
      const topics = response.data.value;

      // Topicのサブスク取得
      const subscriptionsPromises = topics.map((topic) =>
        getTopicSubscriptions(topic.id)
      );
      const subscriptionsResponses = await Promise.all(subscriptionsPromises);
      const allSubscriptions = subscriptionsResponses.flatMap(
        (response) => response.value
      );

      // 応募者のuserIdとサブスクしているuserIdが一致するTopicが存在するかチェック
      const matchingTopic = topics.find((topic) =>
        allSubscriptions.some(
          (sub) => topic.id === sub.topicId && sub.userId === userId
        )
      );

      // 存在すればチャット詳細画面遷移
      if (matchingTopic) {
        navigateTo(`/chats/${matchingTopic.id}`);
      } else {
        try {
          // なければTopicとサブスクを作成
          const newTopicResponse = await createNewTopic(
            loggedInUser.id,
            userId,
            organizationState.name
          );
          console.log('新しいトピックが正常に作成されました:');

          navigateTo(`/chats/${newTopicResponse.data.id}`);
          setIsLoading(false);
        } catch (createError) {
          console.error('トピックの作成中にエラーが発生しました:', createError);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
      setIsLoading(false);
    }
  };

  const rows = useMemo(() => {
    return orders.map((order, i) => {
      return {
        appointmentDate: order.customFields?.appointmentDate,
        appointmentTime: order.customFields?.appointmentTime,
        birthday: order.customer?.user?.customFields?.birthday,
        createdAt: order.createdAt,
        gender: order.customer?.user?.customFields?.gender,
        id: i,
        name: `${order.customer?.user?.customFields?.familyName ?? ''} ${
          order.customer?.user?.customFields?.firstName ?? ''
        }`,
        orderId: order.id,
        product: products?.find((p) => p.id === order.lineItem?.product),
        rate: undefined as number | undefined, // TODO:仕様が確定したら総合評価を設定する
        snsUrl: order.customFields?.snsUrl,
        socialMediaUrl: order.customer?.user?.customFields?.socialMediaUrl,
        status: order.status,
        userId: order.customer?.user?.id,
      };
    });
  }, [orders, products]);

  const columns: GridColDef<(typeof rows)[0]>[] = [
    ...(mode === 'user'
      ? []
      : mode === 'admin'
      ? [
          {
            field: 'org',
            headerName: '応募先店舗',
            renderCell: (params) => {
              const product = params.row.product;
              if (!product?.organization?.name) {
                return <Typography>-</Typography>;
              }
              return <Typography>{product.organization.name}</Typography>;
            },
            width: 180,
          } as GridColDef<(typeof rows)[0]>,
          {
            field: 'product',
            headerName: '応募プラン',
            renderCell: (params) => {
              const product = params.row.product;
              if (!product) {
                return <Typography>-</Typography>;
              }
              return (
                <Box whiteSpace="pre-wrap">
                  <Box
                    fontSize="0.7rem"
                    bgcolor={theme.palette.grey[200]}
                    width="fit-content"
                    color={theme.palette.info.dark}
                    px={1}
                    py={0.1}
                    borderRadius={1}
                  >
                    {product.id}
                  </Box>
                  <Box>
                    <Box display="inline-block">
                      {formatTime(product.customFields?.startTime)}-
                      {formatTime(product.customFields?.endTime)}
                    </Box>
                  </Box>
                </Box>
              );
            },
            width: 180,
          } as GridColDef<(typeof rows)[0]>,
        ]
      : [
          {
            field: 'product',
            headerName: '来店日時',
            renderCell: (params) => {
              const product = params.value as Product | null;
              if (!product) {
                return <Typography>-</Typography>;
              }
              return (
                <Box whiteSpace="pre-wrap">
                  <Box>
                    {params.row.appointmentDate && (
                      <Box>{formatDate(params.row.appointmentDate)}</Box>
                    )}
                    <Box display="inline-block">
                      {formatTime(params.row.appointmentTime)}
                    </Box>
                  </Box>
                </Box>
              );
            },
            width: 170,
          } as GridColDef<(typeof rows)[0]>,
        ]),
    {
      field: 'name',
      headerName: '氏名',
      renderCell: (params) => {
        return (
          <Grid
            sx={{
              alignItems: 'center',
              display: 'flex',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
          >
            <Grid
              sx={{
                alignItems: 'center',
                backgroundColor: 'lightblue',
                borderRadius: '50%',
                color: theme.customPalette.white,
                display: 'flex',
                justifyContent: 'center',
                mr: 2,
                p: '5px 5px ',
              }}
            >
              <PersonOutlineIcon />
            </Grid>
            <Grid>
              <Typography variant="body2">{params.row.name}</Typography>
              <Typography variant="body2">
                /{getAge(params.row.birthday)}歳・{params.row.gender}
              </Typography>
            </Grid>
          </Grid>
        );
      },
      renderHeader: () => (
        <Box textAlign="left" lineHeight="1rem">
          <Box>氏名</Box>
          <Box>/ 年齢・性別</Box>
        </Box>
      ),
      width: 230 + (mode === 'user' ? 40 : 0),
    },
    {
      field: 'socialMediaUrl',
      headerName: '利用SNS',
      renderCell: (params) => <SocialMediaIcons urls={params.value} />,
      width: 150 + (mode === 'user' ? 40 : 0),
    },
    {
      field: 'status',
      headerName: '投稿ステータス',
      renderCell: (params) => {
        const isPosted = params.row.snsUrl && params.row.snsUrl !== '';
        const handleIconClick = (
          event: React.MouseEvent<Element>,
          url?: string
        ) => {
          event.stopPropagation();
          if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        };
        return (
          <Grid container justifyContent="center" alignItems="center">
            {isPosted ? (
              <Typography
                variant="body2"
                align="center"
                style={{
                  color: theme.customPalette.lightBlue,
                  cursor: 'pointer',
                }}
                onClick={(e) => handleIconClick(e, params.row.snsUrl)}
              >
                投稿済み
              </Typography>
            ) : (
              <Typography variant="body2" align="center">
                未投稿
              </Typography>
            )}
          </Grid>
        );
      },
      width: 150 + (mode === 'user' ? 40 : 0),
    },
    ...(mode === 'admin'
      ? [
          {
            field: 'status',
            headerName: 'ステータス',
            renderCell: (params) => {
              return (
                <Typography>
                  {params.row.status === 'PENDING'
                    ? '未対応'
                    : params.row.status === 'ACCEPTED'
                    ? '採用'
                    : params.row.status === 'PROCESSING'
                    ? '評価待ち'
                    : params.row.status === 'CLOSED'
                    ? '評価済み'
                    : params.row.status === 'CANCELED'
                    ? '不採用・キャンセル'
                    : '-'}
                </Typography>
              );
            },
            width: 140,
          } as GridColDef<(typeof rows)[0]>,
        ]
      : [
          {
            field: 'createdAt',
            headerName: mode === 'user' ? '最終来店日時' : '応募日時',
            renderCell: (params) => {
              if (mode === 'user') {
                const product = params.row.product;
                if (
                  !product ||
                  params.row.status === 'CANCELED' ||
                  params.row.status === 'PENDING'
                ) {
                  return <Typography>-</Typography>;
                }
                return (
                  <Grid
                    style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
                  >
                    <Typography fontSize="100%">
                      {getProductDayWeekTitle(product)}
                    </Typography>
                    <Typography fontSize="100%">
                      {formatTime(product.customFields.startTime)}~
                      {formatTime(product.customFields.endTime)}
                    </Typography>
                  </Grid>
                );
              }
              if (!params.value) {
                return (
                  <Grid
                    style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
                  >
                    無効な日付
                  </Grid>
                );
              }
              return (
                <Grid style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                  {params.value}
                </Grid>
              );
            },
            width: 150 + (mode === 'user' ? 40 : 0),
          } as GridColDef<(typeof rows)[0]>,
        ]),
    {
      field: 'actions',
      headerName: '',
      renderCell: (params) => {
        const isFollowing = followList.some((f) => {
          return f === params.row.userId;
        });
        return (
          <Grid>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              sx={{
                fontSize: '0.8rem',
                mr: 2,
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const userId = params.row.userId;
                if (userId) {
                  void handleChatButtonClick(userId);
                }
              }}
            >
              メッセージ
            </Button>
            <FollowButton
              variant="outlined"
              color="secondary"
              size="small"
              sx={{
                backgroundColor: isFollowing
                  ? `${theme.customPalette.borderColor}`
                  : `${theme.customPalette.white}`,
                display: mode === 'admin' ? 'none' : undefined,
                fontSize: '0.8rem',
                paddingLeft: isFollowing ? '8px' : '14px',
                paddingRight: isFollowing ? '8px' : '14px',
              }}
              userId={loggedInUser?.id ?? ''}
              followUserId={params.row.userId ?? ''}
              following={isFollowing}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onChangeFollow={(isFollowing) => {
                if (isFollowing) {
                  setFollowList((prev) => [...prev, params.row.userId ?? '']);
                } else {
                  setFollowList((prev) =>
                    prev.filter((f) => f !== (params.row.userId ?? ''))
                  );
                }
              }}
            />
          </Grid>
        );
      },
      width: 200 + (mode === 'admin' ? -90 : 0),
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          /* cspell:disable-next-line */
          '.MuiBox-root.css-19kzrtu': {
            padding: 0,
          },
        }}
      ></GlobalStyles>
      <Grid
        container
        sx={{
          '& .MuiDataGrid-root .MuiDataGrid-columnHeaders': {
            backgroundColor: theme.customPalette.borderColor,
            p: '0px !important',
          },
          border: 'none',
          height: 550,
          padding: 0,
          width: '100%',
        }}
      >
        <DataGrid
          rows={rows}
          rowHeight={90}
          columns={columns}
          disableColumnMenu
          checkboxSelection
          isRowSelectable={() => !!setSelectedIDs}
          onRowClick={(param) => {
            if (mode === 'admin') {
              navigateTo(`/orders/${param.row.orderId}`);
              return;
            }
            if (mode === 'user') {
              navigateTo(`/users/${param.row.userId}`);
              return;
            }
            navigateTo(`/orders/${param.row.orderId}`);
          }}
          rowSelectionModel={selectionIDs}
          onRowSelectionModelChange={(newSelection: GridRowId[]) => {
            setSelectedIDs?.(newSelection as string[]);
          }}
          sx={{
            '.MuiDataGrid-cell': {
              ':focus-within': { outline: 'none' },
              cursor: 'pointer',
            },
            '.MuiDataGrid-columnHeader': {
              ':focus-within': { outline: 'none' },
            },
            border: 'none',
          }}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 20, 50]}
          localeText={{ noRowsLabel: 'データがありません' }}
          hideFooterSelectedRowCount
          slotProps={{
            pagination: {
              labelRowsPerPage: (
                <label style={{ fontSize: '0.8rem' }}>1ページの表示数</label>
              ),
              showFirstButton: true,
              showLastButton: true,
            },
          }}
          paginationMode={paginationModel ? 'server' : undefined}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          loading={isLoading || loadingProp}
        />
      </Grid>
    </>
  );
};
